// ClubPage.js — /clubs/:slug, one club's Premier League history from its own
// side: record, its scoreline grid (goals scored across, conceded up), the
// results it has had only once, biggest wins and defeats, most common
// results, season by season, and its head-to-head record against every
// opponent (each linking to that head-to-head page).

import React, { useMemo, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import SEO from "./components/SEO";
import AdSlot from "./components/AdSlot";
import NotFoundPage from "./NotFoundPage";
import ScoreGrid from "./components/ScoreGrid";
import { MatchRows, Tile } from "./components/StatBits";
import { CtaRow, H2, InternalLink, P, PageHeader, ProsePage } from "./components/Prose";
import { BRAND } from "./theme";
import { displayName, formatDate } from "./components/heatmap/utils";
import { seasonPath } from "./components/clubs/seasons";
import { h2hPath } from "./components/clubs/h2h";
import { matchText, useSeededQuery } from "./components/scorelines/data";
import StatTable from "./components/StatTable";
import {
  clubInfo,
  clubPath,
  clubStats,
  POINT_DEDUCTIONS,
  pointsFor,
  possessive,
  resultLabel,
  scorelineKey,
  seasonIdStart,
  seasonText,
  teamQuery,
} from "./components/clubs/data";

const SITE_URL = "https://premierleaguescorigami.com";

export default function ClubRoute() {
  const { slug = "" } = useParams();
  const teams = useSeededQuery("teams=1");
  if (teams.error) return <ProsePage><P>The club couldn't be loaded right now. Please try again shortly.</P></ProsePage>;
  if (!teams.data) return <LoadingPage />;
  const record = (teams.data.items || []).find((t) => clubInfo(t.team).slug === slug);
  if (!record) return <NotFoundPage />;
  // Keyed so moving between clubs starts from fresh state.
  return <ClubPage key={record.team} record={record} allTeams={teams.data.items} />;
}

function LoadingPage() {
  return (
    <ProsePage>
      <SEO title="Premier League clubs" description="A Premier League club's record since 1992: results, biggest wins and defeats, every scoreline and every season." path="/clubs" />
      <PageHeader eyebrow={<InternalLink to="/clubs">All clubs</InternalLink>} title="Loading…" />
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} height={40} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />
      ))}
    </ProsePage>
  );
}

function ClubPage({ record, allTeams }) {
  const club = clubInfo(record.team);
  const list = useSeededQuery(teamQuery(record.team));
  const stats = useMemo(() => (list.data ? clubStats(record.team, list.data.items || []) : null), [list.data, record.team]);
  const firstSeason = seasonText(Number(record.firstDate.slice(0, 4)) - (Number(record.firstDate.slice(5, 7)) < 8 ? 1 : 0));
  const lastSeasonStart = seasonIdStart(record.lastSeasonId);
  const currentStart = Math.max(...allTeams.map((t) => seasonIdStart(t.lastSeasonId)));
  const isCurrent = lastSeasonStart === currentStart;
  const S = club.short;
  const Ss = possessive(S);

  const title = `${possessive(club.name)} Premier League record`;
  const lede =
    `${club.name} have played ${record.matches.toLocaleString()} Premier League matches across ${record.seasons} season${record.seasons === 1 ? "" : "s"} since ${firstSeason}: ` +
    `${record.won.toLocaleString()} wins, ${record.drawn.toLocaleString()} draws and ${record.lost.toLocaleString()} defeats, ` +
    `scoring ${record.goalsFor.toLocaleString()} goals and conceding ${record.goalsAgainst.toLocaleString()}.` +
    (isCurrent ? "" : ` Their most recent Premier League season was ${seasonText(lastSeasonStart)}.`);
  // The longest title and summary that fit what Google shows (~65 and ~160 characters).
  const seoTitle = [`${possessive(club.name)} Premier League record: results and biggest wins`, `${possessive(club.name)} Premier League record and biggest wins`].find(
    (t) => t.length <= 65
  ) || `${possessive(club.name)} Premier League record`;
  const base = `${possessive(club.name)} Premier League record since ${firstSeason}: ${record.won} wins, ${record.drawn} draws and ${record.lost} defeats`;
  const description =
    [`${base}. Biggest wins and defeats, every scoreline and every season.`, `${base}, plus biggest wins and defeats and every season.`].find((d) => d.length <= 160) ||
    `${base}.`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Clubs", item: `${SITE_URL}/clubs` },
        { "@type": "ListItem", position: 3, name: club.name, item: `${SITE_URL}${clubPath(record.team)}` },
      ],
    },
    { "@context": "https://schema.org", "@type": "SportsTeam", name: club.name, sport: "Soccer", url: `${SITE_URL}${clubPath(record.team)}` },
  ];

  const tiles = [
    { value: record.matches.toLocaleString(), label: "Matches" },
    { value: record.won.toLocaleString(), label: "Won" },
    { value: record.drawn.toLocaleString(), label: "Drawn" },
    { value: record.lost.toLocaleString(), label: "Lost" },
  ];

  return (
    <ProsePage>
      <SEO
        title={seoTitle}
        description={description}
        path={clubPath(record.team)}
        jsonLd={jsonLd}
      />
      <Box sx={{ display: "flex", gap: { xs: 2, sm: 3 }, alignItems: "flex-start" }}>
        {record.image && (
          <Box component="img" src={record.image} alt={`${club.name} crest`} sx={{ width: { xs: 52, sm: 76 }, height: "auto", flexShrink: 0, mt: { xs: 3.5, sm: 4 } }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <PageHeader eyebrow={<InternalLink to="/clubs">All clubs</InternalLink>} title={title} lede={lede} />
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: 1.25, mb: 1 }}>
        {tiles.map((t) => (
          <Tile key={t.label} {...t} />
        ))}
      </Box>

      {!stats ? (
        list.error ? (
          <P>{Ss} matches couldn't be loaded right now. Please try again shortly.</P>
        ) : (
          Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={40} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />)
        )
      ) : (
        <>
          <H2 id="grid">{Ss} scoreline grid</H2>
          <P>
            Every Premier League result from {Ss} side: goals scored along the bottom, goals conceded up the side. Each
            square counts the matches that ended that way, and empty squares are results {S} have never had. Select a
            square for that scoreline's page.
          </P>
          <ScoreGrid
            counts={new Map([...stats.cells].map(([key, c]) => [key, c.count]))}
            maxGoals={stats.maxGoals}
            xLabel="Goals scored"
            yLabel="Goals conceded"
            labelFor={(gf, ga, n) => `${club.short} ${resultLabel(gf, ga)}: ${n.toLocaleString()} time${n === 1 ? "" : "s"}`}
          />

          {stats.oneOffs.length > 0 && (
            <>
              <H2 id="one-offs">Results {S} have had only once</H2>
              <P>
                {stats.oneOffs.length} of {Ss} Premier League results have happened just once.
                {stats.lastFirst &&
                  ` The most recent new one was ${matchText(stats.lastFirst)} on ${formatDate(stats.lastFirst.utcDate)}.`}
              </P>
              <MatchRows rows={stats.oneOffs.map((m) => matchRow(m, resultLabel(m.gf, m.ga)))} />
            </>
          )}

          <H2 id="biggest-wins">Biggest wins</H2>
          <MatchRows rows={stats.biggestWins.map((m) => matchRow(m, `${m.gf - m.ga}-goal win`))} />
          <H2 id="heaviest-defeats">Heaviest defeats</H2>
          <MatchRows rows={stats.heaviestDefeats.map((m) => matchRow(m, `${m.ga - m.gf}-goal defeat`))} />
          <H2 id="highest-scoring">Highest-scoring matches</H2>
          <MatchRows rows={stats.highestScoring.map((m) => matchRow(m, `${m.gf + m.ga} goals`))} />

          <AdSlot position="article" />

          <H2 id="most-common">{Ss} most common results</H2>
          <MatchRows
            rows={stats.common.map((c, i) => ({
              date: `#${i + 1}`,
              text: resultLabel(c.gf, c.ga),
              badge: `${c.count.toLocaleString()} · ${((c.count / stats.totals.played) * 100).toFixed(1)}%`,
              to: `/scorelines/${scorelineKey(c.gf, c.ga)}`,
            }))}
          />

          <H2 id="seasons">Season by season</H2>
          <StatTable
            head={["Season", "P", "W", "D", "L", "GF", "GA", "GD", "Pts"]}
            rows={stats.seasons.map((s) => [
              <RouterLink key={s.season} to={seasonPath(s.season)}>
                {seasonText(s.season)}
              </RouterLink>,
              s.played,
              s.won,
              s.drawn,
              s.lost,
              s.gf,
              s.ga,
              `${s.gf - s.ga > 0 ? "+" : ""}${s.gf - s.ga}`,
              `${pointsFor(record.team, s.won, s.drawn, s.season)}${POINT_DEDUCTIONS.some((d) => d.team === record.team && d.season === s.season) ? "*" : ""}`,
            ])}
          />
          {POINT_DEDUCTIONS.filter((d) => d.team === record.team).map((d) => (
            <Typography key={d.season} sx={{ color: BRAND.textSecondary, fontSize: 13.5, mt: -1, mb: 2 }}>
              * {seasonText(d.season)}: {d.points} points deducted for {d.reason}.
            </Typography>
          ))}

          <H2 id="opponents">Head to head</H2>
          <P>
            {possessive(club.name)} record against every club they have met in the Premier League, most meetings first. Select an
            opponent for the full head to head.
          </P>
          <Opponents team={record.team} opponents={stats.opponents} />
        </>
      )}

      <CtaRow
        links={[
          { to: "/clubs", label: "All clubs", primary: true },
          { to: "/", label: "Explore the grid" },
          { to: "/scorelines", label: "Every scoreline" },
        ]}
      />
    </ProsePage>
  );
}

const SHOW_OPPONENTS = 10;

// Every opponent, most meetings first, each linking to the head to head.
function Opponents({ team, opponents }) {
  const [all, setAll] = useState(false);
  const shown = all ? opponents : opponents.slice(0, SHOW_OPPONENTS);
  return (
    <>
      <StatTable
        head={["Opponent", "P", "W", "D", "L"]}
        rows={shown.map((o) => [
          <RouterLink key={o.team} to={h2hPath(team, o.team)}>
            v {displayName(o.team)}
          </RouterLink>,
          o.played,
          o.won,
          o.drawn,
          o.lost,
        ])}
      />
      {opponents.length > SHOW_OPPONENTS && !all && (
        <Button onClick={() => setAll(true)} sx={{ color: BRAND.cyan, fontWeight: 700, textTransform: "none", mt: -1, mb: 2 }}>
          Show all {opponents.length} opponents
        </Button>
      )}
    </>
  );
}

const matchRow = (m, badge) => ({
  date: formatDate(m.utcDate),
  text: matchText(m),
  badge,
  to: `/scorelines/${scorelineKey(m.gf, m.ga)}`,
});
